<script lang="ts">
  import { 
    Crown, 
    Sparkles, 
    Zap, 
    Building2, 
    Check, 
    ArrowRight, 
    ShieldCheck, 
    QrCode, 
    Loader2, 
    ArrowLeft,
    CheckCircle2
  } from "lucide-svelte";
  import * as Dialog from "$lib/components/ui/dialog/index.js";
  import { Button } from "$lib/components/ui/button/index.js";
  import { Switch } from "$lib/components/ui/switch/index.js";
  import { Badge } from "$lib/components/ui/badge";
  import QRCode from '@castlenine/svelte-qrcode';
  
  import { toast } from "svelte-sonner";

  interface Props {
    auth: any,
    activeSubscription: {
      plan_name: string;
      plan_key: string;
      is_trial: boolean;
      days_left: number;
      expires_at: string;
      pv_limit: number;
      storage_limit_mb: number;
    };
    plans?: any[];
    siteRegion?: string;
    tl?: any;
    t: (path: string, params?: Record<string, any>) => string;
  }

  let {
    auth, 
    activeSubscription, 
    plans = [], 
    siteRegion = 'CHINA', 
    tl = {}, 
    t
  }: Props = $props();

  let upgradeDialogOpen = $state(false);
  let step = $state<'select' | 'pay'>('select');
  let selectedPlanId = $state('growth');
  let billingCycle = $state<'monthly' | 'yearly'>('monthly');

  // 支付过程状态
  let isSubmitting = $state(false);
  let checkoutData = $state<{
    invoice_id?: string;
    transaction_id?: string;
    provider?: string;
    region?: string;
    pay_data?: { qr_code_url?: string; client_secret?: string };
  } | null>(null);

  let pollTimer: any = null;
  let isPaidSuccess = $state(false);

  // 图标映射
  const planIcons: Record<string, any> = {
    starter: Zap,
    growth: Sparkles,
    pro: Crown,
    enterprise: Building2
  };

  function handleSelectPlan(id: string) {
    selectedPlanId = id;
  }

  function getCsrfToken(): string {
    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta ? meta.getAttribute('content') || '' : '';
  }

  // 重置对话框状态
  function resetState() {
    stopPolling();
    step = 'select';
    isSubmitting = false;
    checkoutData = null;
    isPaidSuccess = false;
  }

  // 开启二维码轮询
  function startPollingStatus(txId: string) {
    stopPolling();
    pollTimer = setInterval(async () => {
      try {
        const res = await fetch(`/checkout/status?transaction_id=${txId}`);
        const data = await res.json();
        if (data.paid) {
          stopPolling();
          isPaidSuccess = true;
          toast.success("支付成功！套餐已自动生效");
          setTimeout(() => {
            upgradeDialogOpen = false;
            window.location.reload();
          }, 1500);
        }
      } catch (err) {
        console.error("Polling error:", err);
      }
    }, 2000);
  }

  function stopPolling() {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  }

  // 发起 Checkout 提交
  async function handleCheckout() {
    if (!selectedPlanId) return;
    isSubmitting = true;

    try {
      const res = await fetch('/checkout', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken()
        },
        body: JSON.stringify({
          plan_id: selectedPlanId,
          billing_cycle: billingCycle
        })
      });

      if (!res.ok) throw new Error("网络请求失败");

      const data = await res.json();
      checkoutData = data;
      step = 'pay';

      const checkoutUrl = data.pay_data?.checkout_url || data.pay_data?.checkoutUrl;

      // 1. 国内微信/支付宝：展示二维码并开启轮询
      if (['wechat', 'alipay'].includes(data.provider)) {
        if (data.transaction_id) startPollingStatus(data.transaction_id);
      } 
      // 2. Waffo 聚合/海外支付：直接跳转 Waffo 托管收银台
      else if (data.provider === 'waffo' || checkoutUrl) {
        if (data.transaction_id) startPollingStatus(data.transaction_id);
        
        // 延迟 500ms 跳转，留出 UI 状态切换时间
        setTimeout(() => {
          window.location.href = checkoutUrl;
        }, 500);
      } 
      // 3. 其他 Drop-in 组件 (如 Airwallex)
      else if (data.provider === 'airwallex') {
        // TODO: 初始化 Airwallex Drop-in
      }
    } catch (err: any) {
      toast.error(err.message || "下单失败，请稍后再试");
    } finally {
      isSubmitting = false;
    }
  }
</script>

{#snippet discountNotice(formattedPrice: string)}
  {@const numericPrice = Number(String(formattedPrice).replace(/[^\d.]/g, ''))}
  {#if !isNaN(numericPrice) && numericPrice > 0}
    {@const currencySymbol = String(formattedPrice).match(/^[^\d]+/)?.[0] || '¥'}
    {@const monthlyEquiv = (numericPrice * 0.83).toFixed(0)}
    <p class="text-[11px] text-emerald-600 dark:text-emerald-400 font-medium leading-none mt-1">
      {t('dashboard.upgrade_modal.yearly_breakdown', { symbol: currencySymbol, price: monthlyEquiv })}
    </p>
  {/if}
{/snippet}

<!-- ==================== 💳 1. 租户套餐 Banner ==================== -->
<div class="relative overflow-hidden rounded-xl border bg-card p-4 md:p-5 shadow-sm">
  <div class="flex flex-col md:flex-row md:items-center justify-between gap-4 relative z-10">
    <div class="flex items-start md:items-center gap-3">
      <div class="p-2.5 rounded-xl bg-primary/10 text-primary shrink-0">
        {#if activeSubscription.is_trial}
          <Sparkles class="size-6" />
        {:else}
          <Crown class="size-6" />
        {/if}
      </div>
      <div>
        <div class="flex items-center gap-2">
          <h2 class="text-base font-bold tracking-tight">
            {t('dashboard.banner.current_plan')} 
            <span class="text-primary">{activeSubscription.plan_name}</span>
          </h2>
          {#if activeSubscription.is_trial}
            <span class="inline-flex items-center rounded-full bg-secondary px-2.5 py-0.5 text-xs font-semibold text-secondary-foreground border">
              {t('dashboard.banner.trial_remaining', { days: activeSubscription.days_left })}
            </span>
          {:else}
            <span class="inline-flex items-center rounded-full bg-emerald-500/10 px-2.5 py-0.5 text-xs font-semibold text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
              {t('dashboard.banner.active_until', { expires_at: activeSubscription.expires_at })}
            </span>
          {/if}
        </div>
        <p class="text-xs text-muted-foreground mt-1">
          {#if activeSubscription.is_trial}
            {t('dashboard.banner.trial_tip')}
          {:else}
            {t('dashboard.banner.quota_tip', { 
              pv_limit: activeSubscription.pv_limit.toLocaleString(), 
              storage_limit_mb: activeSubscription.storage_limit_mb 
            })}
          {/if}
        </p>
      </div>
    </div>

    
    {#if activeSubscription.is_trial}
    <div class="flex items-center gap-3 shrink-0">
      <Button 
        onclick={() => { resetState(); upgradeDialogOpen = true; }}
        class="font-medium shadow-sm"
      >
        <Crown class="size-4 mr-1.5" />
        {activeSubscription.is_trial ? t('dashboard.banner.btn_upgrade') : t('dashboard.banner.btn_renew')}
      </Button>
      { auth?.user }
    </div>
    {/if}
  </div>
</div>

<!-- ==================== 💳 套餐升级与收银台 Dialog 弹窗 ==================== -->
<Dialog.Root bind:open={upgradeDialogOpen} onOpenChange={(open) => !open && stopPolling()}>
  <Dialog.Content class="w-[95vw] xl:max-w-[1140px] max-h-[95vh] p-0 gap-0 overflow-hidden rounded-2xl border-border/80 shadow-2xl flex flex-col">
    
    <!-- 头部区域 -->
    <div class="px-6 pl-6 pr-12 pt-3 pb-2.5 bg-muted/20 border-b border-border/60 text-center relative shrink-0">
      <div class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-semibold mb-0.5">
        <Sparkles class="size-2.5" />
        <span>{tl.upgrade_modal?.badge || t('dashboard.upgrade_modal.badge')}</span>
      </div>
      <Dialog.Title class="text-base font-bold tracking-tight">
        {step === 'select' ? (tl.upgrade_modal?.title || t('dashboard.upgrade_modal.title')) : "完成订单支付"}
      </Dialog.Title>

      {#if step === 'select'}
        <!-- 年付 / 月付 切换器 -->
        <div class="flex items-center justify-center gap-2 mt-1.5">
          <span class="text-xs font-medium {billingCycle === 'monthly' ? 'text-foreground font-semibold' : 'text-muted-foreground'}">
            {t('dashboard.upgrade_modal.monthly_billing')}
          </span>
          <Switch 
            checked={billingCycle === 'yearly'} 
            onCheckedChange={(checked) => billingCycle = checked ? 'yearly' : 'monthly'}
            size="sm"
          />
          <span class="text-xs font-medium flex items-center gap-1 {billingCycle === 'yearly' ? 'text-foreground font-semibold' : 'text-muted-foreground'}">
            {t('dashboard.upgrade_modal.yearly_billing')}
            <Badge variant="secondary" class="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20 text-[10px] font-bold px-1.5 py-0 h-4">
              {t('dashboard.upgrade_modal.save_discount')}
            </Badge>
          </span>
        </div>
      {/if}
    </div>

    <!-- 主体区域 -->
    {#if step === 'select'}
      <!-- 步骤 1：套餐选择 -->
      <div class="p-3 sm:p-4 overflow-y-auto flex-1 flex items-center">
        <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 w-full">
          {#each plans as plan (plan.id)}
            {@const Icon = planIcons[plan.id] || Zap}
            {@const isSelected = selectedPlanId === plan.id}
            {@const isPopular = plan.popular}

            <div 
              role="button"
              tabindex="0"
              onclick={() => handleSelectPlan(plan.id)}
              onkeydown={(e) => (e.key === 'Enter' || e.key === ' ') && handleSelectPlan(plan.id)}
              class="relative rounded-xl border p-3.5 cursor-pointer transition-all duration-200 flex flex-col justify-between group h-full
                {isSelected 
                  ? 'border-primary ring-2 ring-primary/20 bg-primary/[0.02] shadow-md' 
                  : 'border-border/60 hover:border-border hover:shadow-sm bg-card'
                }
                {isPopular && !isSelected ? 'border-primary/50' : ''}"
            >
              {#if isPopular}
                <div class="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm flex items-center gap-1 z-10 whitespace-nowrap">
                  <Sparkles class="size-2.5" />
                  {t('dashboard.upgrade_modal.popular_badge')}
                </div>
              {/if}

              <div class="flex flex-col flex-1">
                <div class="flex items-center gap-1.5 mb-1">
                  <div class="p-1 rounded-md {isPopular ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'} shrink-0">
                    <Icon class="size-3.5" />
                  </div>
                  <h4 class="font-bold text-sm tracking-tight truncate">{plan.name}</h4>
                </div>

                <p class="text-[11px] text-muted-foreground leading-tight line-clamp-2 min-h-[28px] mb-1.5">
                  {plan.description}
                </p>
                
                <div class="py-1.5 border-y border-border/40 my-1.5">
                  <div class="flex items-baseline gap-1">
                    <span class="text-xl font-black tracking-tight">
                      {billingCycle === 'yearly' ? plan.formatted_yearly : plan.formatted_monthly}
                    </span>
                    <span class="text-[11px] text-muted-foreground font-medium">
                      {billingCycle === 'yearly' ? plan.period_yearly : plan.period_monthly}
                    </span>
                  </div>
                  
                  <div class="h-[14px]">
                    {#if billingCycle === 'yearly'}
                      {@render discountNotice(plan.formatted_monthly)}
                    {/if}
                  </div>
                </div>

                <ul class="space-y-1 text-xs text-muted-foreground my-1.5 flex-1">
                  {#each plan.feature_items as item}
                    <li class="flex items-start gap-1.5">
                      <Check class="size-3 text-primary shrink-0 mt-0.5" />
                      <span class="leading-none text-[11px]">
                        {#if item.key === 'domains'}
                          {item.count > 100 
                            ? t('dashboard.plans_data.features.domains_unlimited') 
                            : t('dashboard.plans_data.features.domains', { count: item.count })}
                        {:else if item.key === 'storage'}
                          {@const sizeStr = item.mb >= 1024 ? `${item.mb / 1024} GB` : `${item.mb} MB`}
                          {t('dashboard.plans_data.features.storage', { size: sizeStr, max_file: item.max_file })}
                        {:else if item.key === 'bandwidth'}
                          {@const sizeStr = item.gb >= 1000 ? `${item.gb / 1000} TB` : `${item.gb} GB`}
                          {t('dashboard.plans_data.features.bandwidth', { size: sizeStr })}
                        {:else if item.key === 'locales'}
                          {item.count > 100 
                            ? t('dashboard.plans_data.features.locales_unlimited') 
                            : t('dashboard.plans_data.features.locales', { count: item.count })}
                        {:else if item.key === 'forms'}
                          {item.count > 100000 
                            ? t('dashboard.plans_data.features.forms_unlimited') 
                            : t('dashboard.plans_data.features.forms', { count: item.count })}
                        {:else if item.key === 'team'}
                          {item.count > 100 
                            ? t('dashboard.plans_data.features.team_unlimited') 
                            : t('dashboard.plans_data.features.team', { count: item.count })}
                        {/if}
                      </span>
                    </li>
                  {/each}
                </ul>
              </div>

              <Button 
                variant={isSelected ? "default" : (isPopular ? "secondary" : "outline")} 
                size="sm"
                class="w-full text-xs font-semibold transition-all mt-1.5 h-7"
              >
                {isSelected 
                  ? t('dashboard.upgrade_modal.already_selected') 
                  : t('dashboard.upgrade_modal.select_plan')}
              </Button>
            </div>
          {/each}
        </div>
      </div>

      <!-- 底部结算栏 -->
      <div class="px-5 py-2.5 bg-muted/20 border-t border-border/60 flex flex-col sm:flex-row items-center justify-between gap-2 shrink-0">
        <div class="flex items-center gap-1.5 text-xs text-muted-foreground">
          <ShieldCheck class="size-3.5 text-emerald-500 shrink-0" />
          <span class="text-[11px]">{t('dashboard.upgrade_modal.security_guarantee')}</span>
        </div>

        <div class="flex items-center gap-2 w-full sm:w-auto shrink-0">
          <Button 
            variant="ghost" 
            size="sm" 
            onclick={() => upgradeDialogOpen = false}
            class="text-xs h-7 px-3"
          >
            {t('dashboard.upgrade_modal.cancel')}
          </Button>
          <Button 
            size="sm"
            class="font-semibold px-4 text-xs h-7 w-full sm:w-auto" 
            disabled={!selectedPlanId || isSubmitting} 
            onclick={handleCheckout}
          >
            {#if isSubmitting}
              <Loader2 class="size-3 animate-spin mr-1" />
              正在创建订单...
            {:else}
              {t('dashboard.upgrade_modal.next_checkout')}
              <ArrowRight class="size-3 ml-1" />
            {/if}
          </Button>
        </div>
      </div>

    {:else if step === 'pay'}
      <!-- 步骤 2：收银台页面 -->
      <div class="p-8 flex flex-col items-center justify-center min-h-[380px] my-auto">
        {#if isPaidSuccess}
          <div class="flex flex-col items-center gap-3 text-center">
            <CheckCircle2 class="size-14 text-emerald-500 animate-bounce" />
            <h3 class="text-xl font-bold">支付成功！</h3>
            <p class="text-sm text-muted-foreground">正在为您配置最新套餐权限，请稍候...</p>
          </div>

        {:else if checkoutData?.provider === 'waffo'}
          <!-- Waffo 跳转收银台 -->
          {@const checkoutUrl = checkoutData.pay_data?.checkout_url || checkoutData.pay_data?.checkoutUrl}
          <div class="flex flex-col items-center gap-4 text-center max-w-sm">
            <div class="p-4 rounded-full bg-primary/10 text-primary animate-pulse">
              <ShieldCheck class="size-10" />
            </div>
            
            <div class="space-y-1">
              <h3 class="text-base font-bold">正在为您安全跳转至收银台...</h3>
              <p class="text-xs text-muted-foreground">如果页面没有自动跳转，请点击下方按钮继续支付</p>
            </div>

            {#if checkoutUrl}
              <Button 
                href={checkoutUrl} 
                class="w-full font-semibold shadow-sm mt-2"
              >
                前往 Waffo 支付
                <ArrowRight class="size-4 ml-1.5" />
              </Button>
            {/if}

            <p class="text-[11px] text-muted-foreground flex items-center justify-center gap-1 mt-2">
              <Loader2 class="size-3 animate-spin text-primary" />
              正在等待支付完成，支付成功后页面将自动刷新...
            </p>
          </div>

        {:else if checkoutData?.region === 'CHINA' || ['wechat', 'alipay'].includes(checkoutData?.provider || '')}
          <!-- 国内：微信/支付宝 扫码收银台 -->
          <div class="flex flex-col items-center gap-3">
            <div class="p-4 bg-white rounded-xl shadow-md border border-border flex flex-col items-center">
              {#if checkoutData?.pay_data?.qr_code_url}
                <QRCode data={checkoutData.pay_data.qr_code_url} size={180} />
              {:else}
                <div class="size-[180px] flex items-center justify-center bg-muted">
                  <Loader2 class="size-6 animate-spin text-muted-foreground" />
                </div>
              {/if}
            </div>

            <div class="flex items-center gap-1.5 text-sm font-semibold text-foreground mt-1">
              <QrCode class="size-4 text-primary"/>
              <span>请使用微信扫描上方二维码进行支付</span>
            </div>
            
            <p class="text-xs text-muted-foreground flex items-center gap-1">
              <Loader2 class="size-3 animate-spin text-primary" />
              正在等待支付结果，支付完成后页面将自动跳转...
            </p>
          </div>

        {:else}
          <!-- 海外：Airwallex 或其他 Drop-in Element -->
          <div class="w-full max-w-md flex flex-col items-center gap-4">
            <div id="airwallex-dropin-container" class="w-full border rounded-xl p-6 bg-card text-center">
              <Loader2 class="size-6 animate-spin text-primary mx-auto mb-2" />
              <p class="text-xs text-muted-foreground">正在加载国际安全支付组件...</p>
            </div>
          </div>
        {/if}
      </div>
    {/if}

  </Dialog.Content>
</Dialog.Root>